"use client";

/**
 * Coquille + pipeline d'envoi PARTAGÉS des formulaires de capture de lead
 * (LeadForm). Extraction DRY à comportement strictement identique : trois
 * formulaires lead (QuickLeadForm, TransferQuoteForm, PartnerLeadForm)
 * dupliquaient ~95 % du même code — états loading/success/error, configuration
 * react-hook-form, `onSubmit` (trim → POST /api/contact → lecture data.error →
 * reset au succès), honeypot anti-spam, `<section>` shell, bloc StatusMessage et
 * bouton d'envoi. Tout cela vit ICI, une seule fois.
 *
 * Ce qui DIFFÈRE d'un formulaire à l'autre reste piloté par props, sans rien
 * changer au comportement :
 *  - `fields` : le bloc de champs, rendu via render-prop `(register, errors)`
 *    pour que chaque formulaire conserve EXACTEMENT son markup + sa validation.
 *  - `buildSubmission(values)` : calcule sujet équipe (FR), message (avec la
 *    règle « ≥ 5 car. sinon défaut »), `locationType` et la charge utile
 *    SPÉCIFIQUE (pickup/dropoff/date… côté transfert, companyName/budget côté
 *    partenaire, date côté quick…). LeadForm assemble la charge utile COMMUNE
 *    (firstName, lastName, name, email, phone, subject, message, locationType,
 *    sourcePage, locale, honeypot) et y fusionne `extraPayload`.
 *  - `intro` : bloc de réassurance contextuel (pastille / récap trajet + prix).
 *  - `defaultValues` / `resetValues`, `submitIcon`, `anchorId`, `honeypotId`.
 *
 * Aucune nouvelle mécanique de fetch : le corps POST, l'ordre des clés et le
 * contrat avec /api/contact sont repris à l'identique des trois formulaires
 * d'origine. Garde `react-hook-form`, `FORM_FIELD_CLASS` (via les wrappers) et
 * l'honeypot. Bilingue/trilingue via la prop `locale` + les chaînes `ui`.
 */
import { useState, type ReactNode } from "react";
import {
  useForm,
  type DefaultValues,
  type FieldErrors,
  type FieldValues,
  type UseFormRegister,
  type UseFormWatch,
} from "react-hook-form";
import { usePathname } from "next/navigation";
import { Icon } from "@/components/ui/Icon";
import { Spinner } from "@/components/ui/Spinner";
import { StatusMessage } from "@/components/ui/StatusMessage";
import type { Locale } from "@/config/i18n";
import { FORM_FIELD_CLASS } from "@/config/forms";
import { withAttribution } from "@/lib/attribution-client";

/**
 * Charge utile COMMUNE à tous les leads (clés/valeurs identiques sur les trois
 * formulaires d'origine). `name` est `${firstName} ${lastName}`.trim().
 */
export type LeadBasePayload = {
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string | undefined;
  subject: string;
  message: string;
  locationType: string;
  sourcePage: string;
  locale: Locale;
  honeypot: string;
};

/**
 * Résultat de `buildSubmission` : sujet équipe (FR), message final (la règle
 * « saisie libre ≥ 5 car. sinon message par défaut » est appliquée DANS le
 * wrapper), `locationType` envoyé, et `extraPayload` = clés spécifiques au
 * formulaire (pickup/dropoff/date/time/passengers, companyName/budget, date…).
 */
export type LeadSubmission = {
  subject: string;
  message: string;
  locationType: string;
  /** Clés POST spécifiques au formulaire, fusionnées dans le corps commun. */
  extraPayload?: Record<string, unknown>;
};

/** Chaînes (chrome) communes à tout formulaire lead. */
export type LeadFormUi = {
  eyebrow: string;
  heading: ReactNode;
  subtitle: ReactNode;
  honeypotLabel: string;
  submit: string;
  sending: string;
  successMessage: string;
  errorMessage: string;
  consentNote: string;
  privacyNote: string;
};

export type LeadFormProps<TFieldValues extends FieldValues> = {
  /** Langue du visiteur — envoyée dans le corps + posée en `lang` sur la section. */
  locale: Locale;
  /** Ancre de section (cible des CTA repointés des vues serveur). */
  anchorId: string;
  /** Chaînes localisées (en-tête, bouton, états, notes). */
  ui: LeadFormUi;
  /** Valeurs par défaut react-hook-form. */
  defaultValues: DefaultValues<TFieldValues>;
  /** Valeurs de reset après succès (peut différer des defaults : ex. transfert). */
  resetValues: DefaultValues<TFieldValues>;
  /** Bloc de réassurance contextuel (pastille / récap trajet), sous le sous-titre. */
  intro?: ReactNode;
  /**
   * Bloc de champs : rendu avec `register` + `errors` pour préserver markup +
   * validation. `watch` est exposé pour les indices réactifs (ex. estimation de
   * commission selon le volume choisi côté partenaire) ; les formulaires qui
   * n'en ont pas besoin l'ignorent simplement.
   */
  fields: (
    register: UseFormRegister<TFieldValues>,
    errors: FieldErrors<TFieldValues>,
    watch: UseFormWatch<TFieldValues>,
  ) => ReactNode;
  /**
   * Calcule sujet/message/locationType + charge utile spécifique à partir des
   * valeurs du formulaire. Toute la logique propre au formulaire (trim, défauts,
   * sujet FR, suffixes de message) vit ICI, côté wrapper.
   */
  buildSubmission: (values: TFieldValues) => LeadSubmission;
  /** Icône du bouton d'envoi (varie : mdi:calendar-check, mdi:handshake-outline…). */
  submitIcon: string;
  /** `id` du champ honeypot (varie par formulaire : quick-lead-honeypot…). */
  honeypotId: string;
};

/**
 * Coquille + pipeline d'envoi partagés. Générique sur le type de données du
 * formulaire (`TFieldValues`) ; le champ `honeypot?` y est requis pour
 * l'enregistrer côté react-hook-form.
 */
export default function LeadForm<
  TFieldValues extends FieldValues & { honeypot?: string },
>({
  locale,
  anchorId,
  ui,
  defaultValues,
  resetValues,
  intro,
  fields,
  buildSubmission,
  submitIcon,
  honeypotId,
}: LeadFormProps<TFieldValues>): JSX.Element {
  // Chemin courant : envoyé comme sourcePage (contexte de provenance du lead).
  const pathname = usePathname();

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<TFieldValues>({ defaultValues });

  const onSubmit = async (values: TFieldValues) => {
    setSubmitting(true);
    setSuccess(null);
    setError(null);
    try {
      const { subject, message, locationType, extraPayload } =
        buildSubmission(values);

      const firstName = String(values.firstName ?? "").trim();
      const lastName = String(values.lastName ?? "").trim();
      const fullName = `${firstName} ${lastName}`.trim();

      const basePayload: LeadBasePayload = {
        firstName,
        lastName,
        name: fullName,
        email: String(values.email ?? "").trim(),
        phone: String(values.phone ?? "").trim() || undefined,
        subject,
        message,
        locationType,
        sourcePage: pathname,
        locale,
        honeypot: values.honeypot || "",
      };

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          withAttribution({ ...basePayload, ...extraPayload }),
        ),
      });

      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          typeof responseData?.error === "string"
            ? responseData.error
            : ui.errorMessage,
        );
      }
      setSuccess(ui.successMessage);
      reset(resetValues);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : ui.errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id={anchorId}
      className="scroll-mt-28 bg-white pb-16"
      lang={locale}
    >
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <div className="rounded-3xl border border-black/10 bg-black/[0.03] p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.24em] text-black/55">
            {ui.eyebrow}
          </p>
          <h2 className="mt-2 font-space-age text-lg tracking-[0.2em] text-black">
            {ui.heading}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-black/70">{ui.subtitle}</p>

          {intro}

          <form
            className="mt-6 grid gap-4 md:grid-cols-2"
            onSubmit={handleSubmit(onSubmit)}
          >
            {fields(register, errors, watch)}

            {/* Honeypot anti-spam */}
            <div className="hidden" aria-hidden="true">
              <label htmlFor={honeypotId}>{ui.honeypotLabel}</label>
              <input
                id={honeypotId}
                type="text"
                tabIndex={-1}
                autoComplete="off"
                {...register(
                  "honeypot" as Parameters<UseFormRegister<TFieldValues>>[0],
                )}
              />
            </div>

            <div className="md:col-span-2">
              {success && (
                <div className="mb-4">
                  <StatusMessage type="success" message={success} />
                </div>
              )}
              {error && (
                <div className="mb-4">
                  <StatusMessage type="error" message={error} />
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 text-sm font-semibold text-black transition-colors hover:bg-gold-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Spinner />
                    {ui.sending}
                  </>
                ) : (
                  <>
                    <Icon icon={submitIcon} className="h-4 w-4" />
                    {ui.submit}
                  </>
                )}
              </button>
              <p className="mt-3 text-xs text-black/55">{ui.consentNote}</p>
              <p className="mt-1 text-xs text-black/45">{ui.privacyNote}</p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

/** Classes de champ partagées, ré-exportées pour les wrappers. */
export const LEAD_INPUT_CLASS = FORM_FIELD_CLASS.input;
export const LEAD_LABEL_CLASS = FORM_FIELD_CLASS.label;
